import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  MutationDefinition,
  QueryDefinition,
} from '@reduxjs/toolkit/dist/query/react';
import {
  UseLazyQuery,
  UseMutation,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { useAsyncRequestContext } from 'contexts/AsyncRequestContext';
import { SerializedError } from '@reduxjs/toolkit';
import { ReduxJob } from 'store/slices/apiV1/utility';

export type AsyncRequestHookType<RequestType> =
  | UseLazyQuery<
      QueryDefinition<
        RequestType,
        BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
        string,
        any
      >
    >
  | UseMutation<
      MutationDefinition<
        RequestType,
        BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
        string,
        any
      >
    >;

export type AsyncRequestFunctionType<RequestType, ResponseType> = (
  request: RequestType,
  options?: Partial<Omit<ReduxJob['options'], 'persistenceType'>>
) => Promise<ResponseType | undefined>;

export type AsyncResponseStateType<ResponseType> = {
  data: ResponseType | undefined;
  isLoading: boolean;
  error: FetchBaseQueryError | SerializedError | unknown;
  isSuccess: boolean;
  isError: boolean;
  isFetching: boolean;
  jobMessage?: string | null;
};

/**
 * `useAsyncRequestSession` mimics the behavior of Redux Toolkit Query's lazy queries and mutations.
 * It returns a function that, when invoked, returns a promise. This allows for the handling of asynchronous operations
 * and the utilization of the resulting data once the promise resolves.
 *
 * @param requestHook - RTK Query hook for data fetching.
 * @returns A function that, when invoked, returns a promise and an object containing the request state.
 *
 * The hook leverages the AsyncRequestContext to manage the lifecycle of the request, including adding and removing jobs.
 * The request will persist across the entire session.
 */
export const useAsyncRequestSession = <RequestType, ResponseType>(
  requestHook: AsyncRequestHookType<RequestType>
): [
  AsyncRequestFunctionType<RequestType, ResponseType>,
  AsyncResponseStateType<ResponseType>
] => {
  const isMountedRef = useRef(true);
  const [isFetching, setIsFetching] = useState(false);
  const { handleAddJob, jobQueryInfo } = useAsyncRequestContext();
  const [requestDataQuery, requestQueryInfo] = requestHook();

  const requestData: AsyncRequestFunctionType<RequestType, ResponseType> =
    useCallback(
      async (request, { retrieveDataOnCompletion = true, ...options } = {}) => {
        const asyncOperation = async () => {
          try {
            setIsFetching(true);
            const response = await requestDataQuery(request).unwrap();
            return response;
          } catch (err) {
            if (isMountedRef.current) setIsFetching(false);
          }
          return null;
        };

        const operationResponse = await asyncOperation();

        return new Promise<ResponseType | undefined>((resolve, reject) => {
          const jobData = {
            jobId: operationResponse.jobId,
            resolve: (value: ResponseType | PromiseLike<ResponseType>) => {
              setIsFetching(false);
              resolve(value);
              options.onSuccess?.(value);
            },
            reject: (reason: unknown) => {
              setIsFetching(false);
              reject(reason);
              options.onError?.();
            },
            options: {
              ...options,
              retrieveDataOnCompletion,
            },
          };

          handleAddJob(jobData);
        });
      },
      [requestDataQuery]
    );

  useEffect(
    () => () => {
      isMountedRef.current = false;
      // Remove from session store if the component is unmounted and we don't want to persist the request outside of the component
    },
    []
  );

  return [
    requestData,
    {
      data: jobQueryInfo?.data?.data ?? null,
      isLoading: jobQueryInfo.isLoading || requestQueryInfo.isLoading,
      isSuccess: jobQueryInfo.isSuccess && requestQueryInfo.isSuccess,
      isError: jobQueryInfo.isError || requestQueryInfo.isError,
      isFetching,
      error: jobQueryInfo.error || requestQueryInfo.error,
    },
  ];
};
